import { loading, success, failure } from 'utilities/actions'
import * as actions from './actions'
import { sortParticipants } from './selectors'
import type { Participant } from 'types'

export type State = {
  data: Participant[];
  total: number;
  errors: any[];
  loading: boolean;
  dataFetched: boolean;
  updatingId: string | null;
  updateErrors: any[];
  updateErrorStatus: string | null;
  creating: boolean;
  createErrors: any[];
  createErrorStatus: string | null;
  archivingId: string | null;
  // The outcome of the last write, as one slot: a second one cannot be pending
  // while this holds the first, so no write has to know what the others left
  // behind. Null between writes so the snackbar, which only opens itself on
  // mount, re-mounts and re-shows for consecutive outcomes.
  notification: ParticipantNotification | null;
};

export type ParticipantNotification =
  | { type: 'created' }
  | { type: 'archived'; reference: string }
  | { type: 'unarchived'; reference: string };

export type Action = any & {
  type: string;
};

export const initialState: State = {
  data: [],
  total: 0,
  errors: [],
  loading: false,
  dataFetched: false,
  updatingId: null,
  updateErrors: [],
  updateErrorStatus: null,
  creating: false,
  createErrors: [],
  createErrorStatus: null,
  archivingId: null,
  notification: null,
}

// Merge an updated record back into the list by id and re-apply the AC ordering,
// so a change to its `active` flag moves it to (or back from) the bottom.
const foldParticipant = (
  data: Participant[],
  id: string | undefined,
  updated: Partial<Participant>,
): Participant[] => {
  if (!id) return data
  return sortParticipants(
    data.map(participant =>
      participant.id === id ? { ...participant, ...updated } : participant
    )
  )
}

// A write starts by discarding the previous one's outcome: its rejection, so a
// stale error cannot hold a form open or a snackbar mounted, and its
// notification, so the slot is free for whatever this write produces.
const noWriteOutcome = () => ({
  createErrors: [],
  createErrorStatus: null,
  updateErrors: [],
  updateErrorStatus: null,
  notification: null,
})

const reducer = (state: State, action: Action) => {
  if (state === undefined) { state = initialState }
  switch (action.type) {
    case loading(actions.API_PARTICIPANTS_INDEX):
      // Drop any write outcome left over from a previous visit to the page
      // rather than replaying it on this one.
      return { ...state, ...noWriteOutcome(), errors: [], loading: true }
    case success(actions.API_PARTICIPANTS_INDEX):
      const { data, total } = action
      return {
        ...state,
        data,
        total,
        loading: false,
        dataFetched: true,
        errors: [],
      }
    case failure(actions.API_PARTICIPANTS_INDEX):
      return {
        ...state,
        loading: false,
        errors: action.errors.length ? action.errors : ['Failed to load']
      }
    case actions.API_PARTICIPANTS_UPDATE:
      return { ...state, ...noWriteOutcome(), updatingId: action.id }
    case success(actions.API_PARTICIPANTS_UPDATE):
      return {
        ...state,
        ...noWriteOutcome(),
        data: state.data.map(participant =>
          participant.id === action.context?.id
            ? { ...participant, ...action.data }
            : participant
        ),
        updatingId: null,
      }
    case failure(actions.API_PARTICIPANTS_UPDATE):
      return {
        ...state,
        updatingId: null,
        updateErrors: action.errors?.length ? action.errors : ['Failed to update'],
        // Absent when the request never reached the API; the global error dialog
        // reports those, so leaving it null keeps them out of the snackbar.
        updateErrorStatus: action.status ?? null,
      }
    case actions.API_PARTICIPANTS_CREATE:
      return { ...state, ...noWriteOutcome(), creating: true }
    case success(actions.API_PARTICIPANTS_CREATE):
      return {
        ...state,
        ...noWriteOutcome(),
        data: sortParticipants([...state.data, action.data]),
        total: state.total + 1,
        creating: false,
        notification: { type: 'created' },
      }
    case failure(actions.API_PARTICIPANTS_CREATE):
      return {
        ...state,
        creating: false,
        createErrors: action.errors?.length ? action.errors : ['Failed to create'],
        createErrorStatus: action.status ?? null,
      }
    case actions.API_PARTICIPANTS_ARCHIVE:
    case actions.API_PARTICIPANTS_UNARCHIVE:
      return { ...state, ...noWriteOutcome(), archivingId: action.id }
    case success(actions.API_PARTICIPANTS_ARCHIVE):
      return {
        ...state,
        ...noWriteOutcome(),
        data: foldParticipant(state.data, action.context?.id, action.data),
        archivingId: null,
        notification: { type: 'archived', reference: action.data.reference },
      }
    case success(actions.API_PARTICIPANTS_UNARCHIVE):
      return {
        ...state,
        ...noWriteOutcome(),
        data: foldParticipant(state.data, action.context?.id, action.data),
        archivingId: null,
        notification: { type: 'unarchived', reference: action.data.reference },
      }
    // Failures clear the in-flight flag so the row's action re-enables; the error
    // message itself is surfaced by the global request error handler.
    case failure(actions.API_PARTICIPANTS_ARCHIVE):
    case failure(actions.API_PARTICIPANTS_UNARCHIVE):
      return { ...state, archivingId: null }
    default:
      return state
  }
}

export default reducer
